import type { PieceType, Color } from '@ccc/shared-types';
import { useThemeStore, PIECE_SETS, type PieceSetId } from '../lib/theme';
import { InlinePiece, isInlineSet } from './InlinePieces';

interface PieceProps {
  type: PieceType;
  color: Color;
  size?: number;
  className?: string;
}

const TYPE_TO_LETTER: Record<PieceType, 'P' | 'N' | 'B' | 'R' | 'Q' | 'K'> = {
  pawn: 'P',
  knight: 'N',
  bishop: 'B',
  rook: 'R',
  'mini-rook': 'R',
  queen: 'Q',
  king: 'K',
};

function metaFor(id: PieceSetId) {
  return PIECE_SETS.find((m) => m.id === id);
}

export function Piece({ type, color, size = 64, className }: PieceProps) {
  const pieceSet: PieceSetId = useThemeStore((s) => s.pieceSet);
  const meta = metaFor(pieceSet);
  const letter = TYPE_TO_LETTER[type];
  const scale = type === 'mini-rook' ? 0.78 : 1;

  // Inline themed SVG sets (the 8 Claude Design renderers).
  if (meta?.inline && isInlineSet(pieceSet)) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          transform: scale === 1 ? undefined : `scale(${scale})`,
          userSelect: 'none',
        }}
      >
        <InlinePiece setId={pieceSet} type={type} color={color} />
      </div>
    );
  }

  if (meta?.pngMono) {
    // PNG sets ship one image per piece (the dark / source color) and we
    // render the opposite color with a CSS filter. This keeps the asset
    // count small for AI-generated sets while still showing both sides.
    const src = `/pieces/${pieceSet}/${letter}.png`;
    const isLight = color === 'white';
    const filter = isLight ? 'invert(1) hue-rotate(180deg)' : undefined;
    return (
      <img
        src={src}
        alt={`${color} ${type}`}
        draggable={false}
        width={size}
        height={size}
        className={className}
        style={{
          transform: scale === 1 ? undefined : `scale(${scale})`,
          filter,
          userSelect: 'none',
        }}
      />
    );
  }

  // File-based SVG sets ship paired w/b files.
  const prefix = color === 'white' ? 'w' : 'b';
  const src = `/pieces/${pieceSet}/${prefix}${letter}.svg`;
  return (
    <img
      src={src}
      alt={`${color} ${type}`}
      draggable={false}
      width={size}
      height={size}
      className={className}
      style={{ transform: scale === 1 ? undefined : `scale(${scale})`, userSelect: 'none' }}
    />
  );
}
